import { useState, useEffect } from 'react'
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { isCoachedTrainee } from '../../utils/entitlements'
import { trainingBlockApi } from '../../api'
import type { TrainingTagsGroupedDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES, flattenTrainingTags } from '../../api/trainingBlockApi'
import { TagSelector } from './TagSelector'
import { BlockItemsEditor } from './BlockItemsEditor'
import { emptyItem, itemsFromApi, itemsToApi, type ItemRow } from './blockItemHelpers'
import type { ApiError } from '../../api/client'
import type { TrainingBlockItem } from '../../types/api'
import { Button } from '../ui'
import { TrainingBlockCard } from './TrainingBlockCard'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'

export default function EditTrainingBlock() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { user } = useAuth()
  const blocked = isCoachedTrainee(user)
  const readOnly = searchParams.get('readonly') === '1'

  const [name, setName] = useState('')
  const [availableTags, setAvailableTags] = useState<TrainingTagsGroupedDto>({
    muscles: [], levels: [], genders: [], types: [],
  })
  const [tags, setTags] = useState<string[]>([])
  const [items, setItems] = useState<ItemRow[]>([emptyItem()])
  const [viewItems, setViewItems] = useState<TrainingBlockItem[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (blocked) return
    trainingBlockApi.getTags().then(setAvailableTags).catch(() => {})
  }, [blocked])

  useEffect(() => {
    if (blocked || !id) return
    setLoading(true)
    trainingBlockApi.getBlock(Number(id))
      .then(block => {
        setName(block.name)
        setTags(block.tags ?? [])
        setItems(itemsFromApi(block.items))
        setViewItems(block.items ?? [])
      })
      .catch((err: ApiError) => setError(err.message || t.editTrainingBlock.error.loadFailed))
      .finally(() => setLoading(false))
  }, [id, t, blocked])

  if (blocked) {
    return <Navigate to="/training-blocks" replace />
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!id) return
    setError(null)
    if (!name.trim()) {
      setError(t.createTrainingBlock.error.nameRequired)
      return
    }
    const payloadItems = itemsToApi(items)
    if (payloadItems.length === 0) {
      setError(t.createTrainingBlock.error.noExercises)
      return
    }
    setSubmitting(true)
    try {
      await trainingBlockApi.updateBlock(Number(id), {
        name: name.trim(),
        tags,
        items: payloadItems,
      })
      navigate('/training-blocks')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.createTrainingBlock.error.createFailed)
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="p-5">
        <p style={{ color: theme.text.muted }}>{t.common?.loading ?? 'Loading...'}</p>
      </div>
    )
  }

  const flatTags = flattenTrainingTags(availableTags)
  const tagEmojiMap = Object.fromEntries(flatTags.map(tag => [tag.key, tag.emoji]))
  const tagLabelMap = Object.fromEntries(flatTags.map(tag => [tag.key, tag.label]))

  if (readOnly) {
    return (
      <div className="p-5 pb-24">
        <header className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {t.editTrainingBlock.viewTitle}
          </h1>
        </header>
        <TrainingBlockCard
          name={name}
          items={viewItems}
          tags={tags}
          tagEmojiMap={tagEmojiMap}
          tagLabelMap={tagLabelMap}
        />
      </div>
    )
  }

  return (
    <div className="p-5 pb-24">
      <header className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
          {t.editTrainingBlock.title}
        </h1>
      </header>

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.text.primary }}>
            {t.createTrainingBlock.nameLabel}
          </label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full rounded-xl p-3 focus:outline-none"
            style={{
              backgroundColor: theme.inputBg,
              border: `1px solid ${theme.inputBorder}`,
              color: theme.text.primary,
            }}
          />
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2" style={{ color: theme.text.primary }}>
            {t.createTrainingBlock.tagsLabel}
          </label>
          <TagSelector
            catalog={availableTags}
            categories={BLOCK_TAG_CATEGORIES}
            selected={tags}
            onChange={setTags}
          />
        </div>

        <BlockItemsEditor
          items={items}
          onChange={setItems}
          textColor={theme.text.primary}
          bgColor={theme.cardBg}
          allowReorder
          labels={{
            itemsLabel: t.createTrainingBlock.exercisesLabel,
            addExercise: t.createTrainingBlock.addExercise,
            addToGroup: t.createTrainingBlock.addToGroup,
            notePlaceholder: t.createTrainingBlock.notePlaceholder,
            addNote: t.createTrainingBlock.addNote,
            changeNote: t.createTrainingBlock.changeNote,
            doneNote: t.createTrainingBlock.doneNote,
            exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
            groupLabel: t.createTrainingBlock.groupLabel,
            moveUp: t.createTrainingBlock.moveUp,
            moveDown: t.createTrainingBlock.moveDown,
            ungroup: t.createTrainingBlock.ungroup,
          }}
        />

        {error && (
          <div className="bg-red-500/15 border border-red-500/30 rounded-xl p-4">
            <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
          </div>
        )}

        <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
          {submitting ? t.editTrainingBlock.submitting : t.editTrainingBlock.submit}
        </Button>
      </form>
    </div>
  )
}
